import { useEffect, useState } from "react";
import {
  fetchUsers,
  createUser,
  toggleBlockUser,
  deleteUser,
} from "../api/userApi";
import { toast } from "react-toastify";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [actionId, setActionId] = useState(null);

  // Create user form
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "user",
  });

  /* ================= LOAD USERS ================= */

  const loadUsers = async () => {
    try {
      setLoading(true);

      const data = await fetchUsers();
      setUsers(data);
    } catch (err) {
      console.error("FETCH USERS ERROR:", err);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  /* ================= CREATE USER ================= */

  const handleCreateUser = async (e) => {
    e.preventDefault();

    try {
      setCreating(true);

      await createUser(form);

      toast.success("User created successfully");

      setForm({
        name: "",
        email: "",
        password: "",
        role: "user",
      });

      await loadUsers();
    } catch (err) {
      console.error("CREATE USER ERROR:", err);

      toast.error(
        err.response?.data?.message ||
          "User creation failed"
      );
    } finally {
      setCreating(false);
    }
  };

  /* ================= BLOCK / UNBLOCK ================= */

  const handleBlock = async (id) => {
    try {
      setActionId(id);

      await toggleBlockUser(id);

      toast.success("User status updated");

      await loadUsers();
    } catch (err) {
      console.error("BLOCK USER ERROR:", err);
      toast.error("Failed to update user");
    } finally {
      setActionId(null);
    }
  };

  /* ================= DELETE ================= */

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this user?")) return;

    try {
      setActionId(id);

      await deleteUser(id);

      toast.success("User deleted");

      await loadUsers();
    } catch (err) {
      console.error("DELETE USER ERROR:", err);
      toast.error("Delete failed");
    } finally {
      setActionId(null);
    }
  };

  /* ================= STATUS COLORS ================= */

  const getStatusClasses = (isBlocked) => {
    return isBlocked
      ? "bg-red-100 text-red-700"
      : "bg-green-100 text-green-700";
  };

  /* ================= ROLE COLORS ================= */

  const getRoleClasses = (role) => {
    return role === "admin"
      ? "bg-purple-100 text-purple-700"
      : "bg-blue-100 text-blue-700";
  };

  /* ================= LOADING ================= */

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[250px]">
        <p className="text-sm text-gray-500">
          Loading users...
        </p>
      </div>
    );
  }

  /* ================= UI ================= */

  return (
    <div className="w-full space-y-6 sm:space-y-8">
      {/* ================= PAGE HEADER ================= */}

      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
          Users
        </h1>

        <p className="text-sm text-gray-500 mt-1">
          Manage users and administrator accounts
        </p>
      </div>

      {/* ================= CREATE USER ================= */}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="mb-5">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">
            Create User
          </h2>

          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            Add a new user or administrator account.
          </p>
        </div>

        <form
          onSubmit={handleCreateUser}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          {/* NAME */}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Name
            </label>

            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter name"
              value={form.name}
              onChange={(e) =>
                setForm({
                  ...form,
                  name: e.target.value,
                })
              }
              required
            />
          </div>

          {/* EMAIL */}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Email
            </label>

            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter email"
              type="email"
              value={form.email}
              onChange={(e) =>
                setForm({
                  ...form,
                  email: e.target.value,
                })
              }
              required
            />
          </div>

          {/* PASSWORD */}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Password
            </label>

            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter password"
              type="password"
              value={form.password}
              onChange={(e) =>
                setForm({
                  ...form,
                  password: e.target.value,
                })
              }
              required
            />
          </div>

          {/* ROLE */}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Role
            </label>

            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={form.role}
              onChange={(e) =>
                setForm({
                  ...form,
                  role: e.target.value,
                })
              }
            >
              <option value="user">
                User
              </option>

              <option value="admin">
                Admin
              </option>
            </select>
          </div>

          {/* CREATE BUTTON */}

          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={creating}
              className="w-full md:w-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {creating
                ? "Creating..."
                : "Create User"}
            </button>
          </div>
        </form>
      </div>

      {/* ================= USER LIST ================= */}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-800">
              Users
            </h2>

            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {users.length}{" "}
              {users.length === 1
                ? "user"
                : "users"}{" "}
              found
            </p>
          </div>
        </div>

        {/* ================= DESKTOP TABLE ================= */}

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-100">
                <th className="px-4 py-3 text-left font-semibold text-gray-600">
                  Name
                </th>

                <th className="px-4 py-3 text-left font-semibold text-gray-600">
                  Email
                </th>

                <th className="px-4 py-3 text-left font-semibold text-gray-600">
                  Role
                </th>

                <th className="px-4 py-3 text-left font-semibold text-gray-600">
                  Status
                </th>

                <th className="px-4 py-3 text-left font-semibold text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {users.length > 0 ? (
                users.map((u) => (
                  <tr
                    key={u._id}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                  >
                    {/* NAME */}

                    <td className="px-4 py-4 font-medium text-gray-800">
                      {u.name}
                    </td>

                    {/* EMAIL */}

                    <td className="px-4 py-4 text-gray-600">
                      {u.email}
                    </td>

                    {/* ROLE */}

                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getRoleClasses(
                          u.role
                        )}`}
                      >
                        {u.role}
                      </span>
                    </td>

                    {/* STATUS */}

                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatusClasses(
                          u.isBlocked
                        )}`}
                      >
                        {u.isBlocked
                          ? "Blocked"
                          : "Active"}
                      </span>
                    </td>

                    {/* ACTIONS */}

                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            handleBlock(u._id)
                          }
                          disabled={
                            actionId === u._id
                          }
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium text-white disabled:opacity-50 ${
                            u.isBlocked
                              ? "bg-green-600 hover:bg-green-700"
                              : "bg-yellow-500 hover:bg-yellow-600"
                          }`}
                        >
                          {actionId === u._id
                            ? "..."
                            : u.isBlocked
                            ? "Unblock"
                            : "Block"}
                        </button>

                        {u.role !== "admin" && (
                          <button
                            onClick={() =>
                              handleDelete(
                                u._id
                              )
                            }
                            disabled={
                              actionId === u._id
                            }
                            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium disabled:opacity-50"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-10 text-center text-gray-500"
                  >
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ================= MOBILE CARDS ================= */}

        <div className="md:hidden space-y-3">
          {users.length > 0 ? (
            users.map((u) => (
              <div
                key={u._id}
                className="border border-gray-100 rounded-xl p-4 shadow-sm"
              >
                {/* USER HEADER */}

                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">
                      {u.name}
                    </p>

                    <p className="text-xs text-gray-500 mt-1 break-all">
                      {u.email}
                    </p>
                  </div>

                  <span
                    className={`shrink-0 inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatusClasses(
                      u.isBlocked
                    )}`}
                  >
                    {u.isBlocked
                      ? "Blocked"
                      : "Active"}
                  </span>
                </div>

                {/* ROLE */}

                <div className="mt-4">
                  <p className="text-xs text-gray-500 mb-1">
                    Role
                  </p>

                  <span
                    className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getRoleClasses(
                      u.role
                    )}`}
                  >
                    {u.role}
                  </span>
                </div>

                {/* ACTIONS */}

                <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() =>
                      handleBlock(u._id)
                    }
                    disabled={
                      actionId === u._id
                    }
                    className={`py-2.5 rounded-lg text-sm font-medium text-white disabled:opacity-50 ${
                      u.isBlocked
                        ? "bg-green-600"
                        : "bg-yellow-500"
                    }`}
                  >
                    {actionId === u._id
                      ? "Please wait..."
                      : u.isBlocked
                      ? "Unblock"
                      : "Block"}
                  </button>

                  {u.role !== "admin" ? (
                    <button
                      onClick={() =>
                        handleDelete(u._id)
                      }
                      disabled={
                        actionId === u._id
                      }
                      className="py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                    >
                      Delete
                    </button>
                  ) : (
                    <div />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="py-10 text-center text-gray-500 text-sm">
              No users found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}